import { PayloadAction, createSlice } from '@reduxjs/toolkit';
import { TireConditionType } from '../constants/product/product.constant';

export const imageTypes = [
  'LEFT',
  'FRONT',
  'BACK',
  'INNER',
  'WORKINGMACHINE1',
  'FRONTTIER',
  'BACKTIER',
  'ETC1',
  'ETC2',
  'ETC3',
] as const;

export type tractorImageType = (typeof imageTypes)[number];
export type tractorImageObject = Record<tractorImageType, string>;

interface userSellTractorRequiredValue {
  brand?: string;
  brandId?: string;
  model?: string;
  engineOutputInPs?: number;
  manufacturedYear?: number;
  operationTime?: number;
}

interface userSellTractorSurveyStateType extends userSellTractorRequiredValue {
  isSellTractorSurveyStartedProperly: boolean;
  sellTractorIdForEdit?: string;
  isUserSellOwnTractor?: boolean;
  userOwnTractorId?: string;
  userOwnTractorMarketPrice?: number;
  option?: string;
  optionDetails?: string[];
  workingMachine?: string;
  workingMachineDetails?: string[];
  replacedParts?: string[];
  needRepairParts?: string[];
  frontTireCondition?: TireConditionType;
  rearTireCondition?: TireConditionType;
  information?: string;
  tractorImageUrls: tractorImageObject;
  location?: string;
  hopeSellingPrice?: number;
  directImportYn?: boolean;
  priceNegotiationYn?: boolean;
}

const initialState: userSellTractorSurveyStateType = {
  isSellTractorSurveyStartedProperly: false,
  sellTractorIdForEdit: undefined,
  isUserSellOwnTractor: undefined,
  userOwnTractorId: undefined,
  userOwnTractorMarketPrice: undefined,
  brand: undefined,
  brandId: undefined,
  model: undefined,
  engineOutputInPs: undefined,
  manufacturedYear: undefined,
  operationTime: undefined,
  option: undefined,
  optionDetails: undefined,
  workingMachine: undefined,
  workingMachineDetails: undefined,
  replacedParts: undefined,
  needRepairParts: undefined,
  frontTireCondition: undefined,
  rearTireCondition: undefined,
  information: undefined,
  tractorImageUrls: {
    LEFT: '',
    FRONT: '',
    BACK: '',
    INNER: '',
    WORKINGMACHINE1: '',
    FRONTTIER: '',
    BACKTIER: '',
    ETC1: '',
    ETC2: '',
    ETC3: '',
  },
  location: undefined,
  hopeSellingPrice: undefined,
  directImportYn: undefined,
  priceNegotiationYn: undefined,
};

export const userSellTractorSurveySlice = createSlice({
  name: 'userSellTractorSurvey',
  initialState,
  reducers: {
    resetState: (state) => {
      state.isSellTractorSurveyStartedProperly = false;
      state.sellTractorIdForEdit = undefined;
      state.isUserSellOwnTractor = undefined;
      state.userOwnTractorId = undefined;
      state.userOwnTractorMarketPrice = undefined;
      state.brand = undefined;
      state.brandId = undefined;
      state.model = undefined;
      state.engineOutputInPs = undefined;
      state.manufacturedYear = undefined;
      state.operationTime = undefined;
      state.option = undefined;
      state.optionDetails = undefined;
      state.workingMachine = undefined;
      state.workingMachineDetails = undefined;
      state.replacedParts = undefined;
      state.needRepairParts = undefined;
      state.frontTireCondition = undefined;
      state.rearTireCondition = undefined;
      state.information = undefined;
      state.tractorImageUrls = {
        LEFT: '',
        FRONT: '',
        BACK: '',
        INNER: '',
        WORKINGMACHINE1: '',
        FRONTTIER: '',
        BACKTIER: '',
        ETC1: '',
        ETC2: '',
        ETC3: '',
      };
      state.location = undefined;
      state.hopeSellingPrice = undefined;
      state.directImportYn = undefined;
      state.priceNegotiationYn = undefined;
    },

    changeSellTractorSurveyForEdit: (
      state,
      action: PayloadAction<
        Omit<
          userSellTractorSurveyStateType,
          | 'isSellTractorSurveyStartedProperly'
          | 'isUserSellOwnTractor'
          | 'userOwnTractorId'
          | 'userOwnTractorMarketPrice'
        >
      >,
    ) => {
      state.sellTractorIdForEdit = action.payload.sellTractorIdForEdit;
      state.brand = action.payload.brand;
      state.brandId = action.payload.brandId;
      state.model = action.payload.model;
      state.engineOutputInPs = action.payload.engineOutputInPs;
      state.manufacturedYear = action.payload.manufacturedYear;
      state.operationTime = action.payload.operationTime;
      state.option = action.payload.option;
      state.optionDetails = action.payload.optionDetails;
      state.workingMachine = action.payload.workingMachine;
      state.workingMachineDetails = action.payload.workingMachineDetails;
      state.replacedParts = action.payload.replacedParts;
      state.needRepairParts = action.payload.needRepairParts;
      state.frontTireCondition = action.payload.frontTireCondition;
      state.rearTireCondition = action.payload.rearTireCondition;
      state.information = action.payload.information;
      state.tractorImageUrls = action.payload.tractorImageUrls;
      state.location = action.payload.location;
      state.hopeSellingPrice = action.payload.hopeSellingPrice;
      state.directImportYn = action.payload.directImportYn;
      state.priceNegotiationYn = action.payload.priceNegotiationYn;
    },

    changeIsSellTractorSurveyStartedProperly: (state, action: PayloadAction<boolean>) => {
      state.isSellTractorSurveyStartedProperly = action.payload;
    },

    changeUserOwnTractorId: (state, action: PayloadAction<string>) => {
      state.userOwnTractorId = action.payload;
    },

    changeSellTractorRequiredInputs: (state, action: PayloadAction<userSellTractorRequiredValue>) => {
      state.brand = action.payload.brand;
      state.brandId = action.payload.brandId;
      state.model = action.payload.model;
      state.engineOutputInPs = action.payload.engineOutputInPs;
      state.manufacturedYear = action.payload.manufacturedYear;
      state.operationTime = action.payload.operationTime;
    },

    changeIsUserSellOwnTractor: (state, action: PayloadAction<boolean>) => {
      state.isUserSellOwnTractor = action.payload;
    },

    changeUserOwnTractorMarketPrice: (state, action: PayloadAction<number>) => {
      state.userOwnTractorMarketPrice = action.payload;
    },

    changeOption: (state, action: PayloadAction<string>) => {
      state.option = action.payload;
    },

    changeOptionDetails: (state, action: PayloadAction<string[]>) => {
      state.optionDetails = action.payload;
    },

    changeWorkingMachine: (state, action: PayloadAction<string>) => {
      state.workingMachine = action.payload;
    },

    changeWorkingMachineDetails: (state, action: PayloadAction<string[]>) => {
      state.workingMachineDetails = action.payload;
    },

    changeReplacedParts: (state, action: PayloadAction<string[]>) => {
      state.replacedParts = action.payload;
    },

    changeNeedRepairParts: (state, action: PayloadAction<string[]>) => {
      state.needRepairParts = action.payload;
    },

    changeFrontTireCondition: (state, action: PayloadAction<TireConditionType>) => {
      state.frontTireCondition = action.payload;
    },

    changeRearTireCondition: (state, action: PayloadAction<TireConditionType>) => {
      state.rearTireCondition = action.payload;
    },

    changeInformation: (state, action: PayloadAction<string>) => {
      state.information = action.payload;
    },

    changeTractorImageUrls: (state, action: PayloadAction<{ property: tractorImageType; imageUrl: string }>) => {
      state.tractorImageUrls[action.payload.property] = action.payload.imageUrl;
    },

    changeLocation: (state, action: PayloadAction<string>) => {
      state.location = action.payload;
    },

    changeHopeSellingPrice: (state, action: PayloadAction<number>) => {
      state.hopeSellingPrice = action.payload;
    },

    changeDirectImportYn: (state, action: PayloadAction<boolean>) => {
      state.directImportYn = action.payload;
    },

    changePriceNegotiationYn: (state, action: PayloadAction<boolean>) => {
      state.priceNegotiationYn = action.payload;
    },
  },
});
